import React, { useEffect, useState, useCallback } from "react";
import { Elements } from "@stripe/react-stripe-js";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import apiClient from "../../utils/apiClient";
import { db, auth } from "../../config";
import "./Cards.css";
import Button from "../../components/Button/Button";
import CardDetailsElements from "../../components/CardDetailsElements";
import CardsInfoSkeleton from "./CardsInfoSkeleton";
import Loader from "../../components/Loader";
import { transformDate } from "../../utils/transformDate";

const getApiError = (err) =>
  err.response?.data?.error || err.message || "Something went wrong";

const getStatusLabel = (status) => {
  if (status === "inactive") return "Frozen";
  if (status === "canceled") return "Canceled";
  return "Active";
};

const isCardExpired = (expMonth, expYear) => {
  if (!expMonth || !expYear) return false;
  const now = new Date();
  const expiry = new Date(expYear, expMonth, 0);
  return expiry < now;
};

const Cards = ({ stripePromise = null, accountId = "" }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [ip, setIP] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [line1, setLine1] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [cardholder, setCardholder] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState("");
  const [nonCanceledCount, setNonCanceledCount] = useState(0);
  const [toggleModel, settoggleModel] = useState(false);
  const [cardHolderDate, setCardHolderDate] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  const displayName = cardholder?.fullName || [firstName, lastName].filter(Boolean).join(" ").trim();
  const selectedCard = cards.find((c) => c.id === selectedCardId) || null;

  const loadCards = useCallback(async () => {
    const res = await apiClient.post("list-cards", {});
    const { cardholder: holder, cards: cardList, nonCanceledCount: count } = res.data;
    setCardholder(holder);
    setCards(cardList || []);
    setNonCanceledCount(count || 0);

    if (holder) {
      setCardHolderDate(
        holder.cardHolderDate ? transformDate(new Date(holder.cardHolderDate)) : ""
      );
      if (holder.fullName) {
        const parts = holder.fullName.trim().split(/\s+/);
        setFirstName(parts[0] || "");
        setLastName(parts.slice(1).join(" ") || "");
      }
      if (holder.line1) setLine1(holder.line1);
      if (holder.city) setCity(holder.city);
      if (holder.state) setState(holder.state);
      if (holder.postalCode) setPostalCode(holder.postalCode);
      if (holder.phoneNumber) setPhoneNumber(holder.phoneNumber);
      if (holder.email) setEmail(holder.email);
    }

    const activeCards = (cardList || []).filter((c) => c.status !== "canceled");
    if (activeCards.length > 0) {
      const defaultCard = activeCards.find((c) => c.isDefault) || activeCards[0];
      setSelectedCardId((prev) => {
        if (prev && activeCards.some((c) => c.id === prev)) return prev;
        return defaultCard.id;
      });
    } else {
      setSelectedCardId("");
    }
  }, []);

  useEffect(() => {
    axios
      .get("https://geolocation-db.com/json/")
      .then((res) => setIP(res.data.ip))
      .catch(() => setIP("127.0.0.1"));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCardsPage = async () => {
      if (!auth.currentUser) return;

      setIsLoading(true);
      setError("");

      try {
        const userRef = doc(db, "users", auth.currentUser.uid);
        const [userSnap, balanceRes] = await Promise.all([
          getDoc(userRef),
          apiClient.post("balance", {}),
        ]);

        if (cancelled) return;

        const userData = userSnap.data() || {};
        setEmail(userData.email || "");
        setIsVerified(!!balanceRes.data?.Verified);
        if (userData.firstName) setFirstName(userData.firstName);
        if (userData.lastName) setLastName(userData.lastName);

        await loadCards();
      } catch (err) {
        if (!cancelled) {
          setError(getApiError(err));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCardsPage();

    return () => {
      cancelled = true;
    };
  }, [loadCards]);

  const toggleM = () => {
    setError("");
    setAcceptedTerms(false);
    settoggleModel((prev) => !prev);
  };

  const OrderVirtualCard = async (e) => {
    e.preventDefault();

    const hasHolder = !!cardholder?.cardHolderId;

    if (!hasHolder && !acceptedTerms) {
      setError("You must accept the cardholder terms and privacy policy.");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const payload = hasHolder
        ? {}
        : {
            firstName,
            lastName,
            line1,
            state,
            email,
            postalCode,
            phoneNumber,
            city,
            ip,
            acceptedTerms: true,
          };

      const response = await apiClient.post("cards", payload);

      if (response.data.error) {
        setError(response.data.error);
        return;
      }

      settoggleModel(false);
      await loadCards();
      if (response.data.cardId) {
        setSelectedCardId(response.data.cardId);
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleFreeze = async () => {
    if (!selectedCardId) return;
    setProcessing(true);
    setError("");
    try {
      await apiClient.post("cards/freeze", { cardId: selectedCardId });
      await loadCards();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedCardId) return;
    if (!window.confirm("Cancel this card permanently? This cannot be undone.")) return;
    setProcessing(true);
    setError("");
    try {
      await apiClient.post("cards/cancel", { cardId: selectedCardId });
      await loadCards();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleReplace = async () => {
    if (!selectedCardId) return;
    setProcessing(true);
    setError("");
    try {
      const response = await apiClient.post("cards/replace", { cardId: selectedCardId });
      await loadCards();
      if (response.data.cardId) {
        setSelectedCardId(response.data.cardId);
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setProcessing(false);
    }
  };

  const canAddCard = isVerified && nonCanceledCount < 5 && !isLoading;
  const hasHolder = !!cardholder?.cardHolderId;
  const showCardsPanel = cards.length > 0;

  return (
    <div className="cards-page">
      <header className="cards-header">
        <div>
          <p className="page-eyebrow">Cards</p>
          <h1 className="page-title">Cards</h1>
        </div>
        <Button
          btnName="Add virtual card"
          onClick={toggleM}
          disabled={!canAddCard || processing}
          classe="btn--accent"
        />
      </header>

      {!isLoading && !isVerified && (
        <p className="cards-hint" role="status">
          Complete account verification on the dashboard before ordering a card.
        </p>
      )}

      <div className="cards-grid">
        <section className="cards-panel cards-panel--holder" aria-busy={isLoading}>
          <div className="cards-panel__header">
            <h2 className="cards-panel__title">Cardholder</h2>
            <span className="cards-panel__subtitle">Who this card is issued to</span>
          </div>

          <div className="cards-panel__body">
            {isLoading ? (
              <CardsInfoSkeleton />
            ) : (
              <div className="cards-holder-card">
                <div className="cards-holder-fields">
                  <div className="cards-holder-field">
                    <span className="cards-info-label">Name</span>
                    <span className="cards-info-value">{displayName || "—"}</span>
                  </div>
                  <div className="cards-holder-field">
                    <span className="cards-info-label">Email</span>
                    <span className="cards-info-value cards-info-value--break">
                      {cardholder?.email || email || "—"}
                    </span>
                  </div>
                  <div className="cards-holder-field">
                    <span className="cards-info-label">Created</span>
                    <span className="cards-info-value">{cardHolderDate || "—"}</span>
                  </div>
                </div>
              </div>
            )}

            {!isLoading && !hasHolder && (
              <p className="cards-hint cards-hint--inline">Order a virtual card to get started.</p>
            )}

            {error && !toggleModel && (
              <p className="cards-error" role="alert">
                {error}
              </p>
            )}
          </div>
        </section>

        {showCardsPanel && (
          <section className="cards-panel cards-panel--card">
            <div className="cards-panel__header">
              <h2 className="cards-panel__title">Your virtual cards</h2>
              <span className="cards-panel__subtitle">
                {nonCanceledCount} of 5 active slots used
              </span>
            </div>
            <div className="cards-panel__body">
              <div className="cards-picker" role="tablist" aria-label="Select card">
                {cards.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    role="tab"
                    aria-selected={selectedCardId === card.id}
                    className={`cards-picker__chip${
                      selectedCardId === card.id ? " cards-picker__chip--active" : ""
                    }${card.status === "inactive" ? " cards-picker__chip--frozen" : ""}${
                      card.status === "canceled" ? " cards-picker__chip--canceled" : ""
                    }`}
                    onClick={() => setSelectedCardId(card.id)}
                  >
                    <span className="cards-picker__last4">
                      {card.last4 ? `•••• ${card.last4}` : "Card"}
                    </span>
                    <span
                      className={`cards-status-badge cards-status-badge--${card.status}`}
                    >
                      {getStatusLabel(card.status)}
                    </span>
                    {card.isDefault && card.status !== "canceled" && (
                      <span className="cards-picker__default">Default</span>
                    )}
                  </button>
                ))}
              </div>

              {selectedCard && selectedCard.status !== "canceled" && (
                <>
                  <div className="cards-actions">
                    {selectedCard.status !== "canceled" && (
                      <button
                        type="button"
                        className="cards-action-btn"
                        onClick={handleFreeze}
                        disabled={processing}
                      >
                        {selectedCard.status === "inactive" ? "Unfreeze" : "Freeze"}
                      </button>
                    )}
                    {selectedCard.status !== "canceled" && (
                      <button
                        type="button"
                        className="cards-action-btn cards-action-btn--danger"
                        onClick={handleCancel}
                        disabled={processing}
                      >
                        Cancel
                      </button>
                    )}
                    {(isCardExpired(selectedCard.expMonth, selectedCard.expYear) ||
                      selectedCard.status === "inactive") && (
                      <button
                        type="button"
                        className="cards-action-btn cards-action-btn--accent"
                        onClick={handleReplace}
                        disabled={processing}
                      >
                        Replace
                      </button>
                    )}
                  </div>

                  {stripePromise && accountId ? (
                    <Elements key={`${accountId}-${selectedCardId}`} stripe={stripePromise}>
                      <CardDetailsElements
                        cardId={selectedCardId}
                        frozen={selectedCard.status === "inactive"}
                      />
                    </Elements>
                  ) : (
                    <div className="cards-card-slot cards-card-slot--loading" aria-busy="true">
                      <Loader size={40} />
                    </div>
                  )}
                </>
              )}

              {selectedCard && selectedCard.status === "canceled" && (
                <div className="cards-canceled-notice">
                  <p>This card has been canceled and can no longer be used.</p>
                  {selectedCard.replacedBy && (
                    <button
                      type="button"
                      className="cards-action-btn cards-action-btn--accent"
                      onClick={() => setSelectedCardId(selectedCard.replacedBy)}
                    >
                      View replacement card
                    </button>
                  )}
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {toggleModel && (
        <>
          <div
            className="cards-overlay"
            onClick={() => settoggleModel(false)}
            aria-hidden="true"
          />
          <form onSubmit={OrderVirtualCard} className="cards-form">
            <div className="cards-form__header">
              <h2>{hasHolder ? "Add virtual card" : "Order virtual card"}</h2>
              <button
                type="button"
                className="cards-form__close"
                onClick={() => settoggleModel(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {hasHolder ? (
              <p className="cards-form__hint">
                A new virtual card will be issued to your existing cardholder profile.
              </p>
            ) : (
              <div className="cards-form__grid">
                <div className="cards-form__field">
                  <label htmlFor="firstName">First name</label>
                  <input
                    id="firstName"
                    placeholder="Jane"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="cards-form__field">
                  <label htmlFor="lastName">Last name</label>
                  <input
                    id="lastName"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <div className="cards-form__field cards-form__field--full">
                  <label htmlFor="email">Email</label>
                  <input id="email" value={email} readOnly required />
                </div>
                <div className="cards-form__field">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    placeholder="+1 555 000 0000"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
                <div className="cards-form__field">
                  <label htmlFor="line1">Street address</label>
                  <input
                    id="line1"
                    placeholder="123 Main St"
                    value={line1}
                    onChange={(e) => setLine1(e.target.value)}
                    required
                  />
                </div>
                <div className="cards-form__field">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    placeholder="San Francisco"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    required
                  />
                </div>
                <div className="cards-form__field">
                  <label htmlFor="state">State</label>
                  <input
                    id="state"
                    placeholder="CA"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    required
                  />
                </div>
                <div className="cards-form__field cards-form__field--full">
                  <label htmlFor="postalCode">Postal code</label>
                  <input
                    id="postalCode"
                    placeholder="94102"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            {!hasHolder && (
              <label className="cards-form__terms">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                />
                <span>
                  I agree to the{" "}
                  <a
                    href="https://stripe.com/legal/issuing/celtic-spend-card-authorized-user-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Authorized User Terms
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://stripe.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            )}

            {error && <p className="cards-form__error">{error}</p>}

            <button
              type="submit"
              className="cards-form__submit"
              disabled={processing || (!hasHolder && !acceptedTerms)}
            >
              {processing ? (
                <Loader size={22} />
              ) : hasHolder ? (
                "Add virtual card"
              ) : (
                "Order virtual card"
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default Cards;
